import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from '../layouts/AppLayout';
import LoginPage from '../pages/LoginPage';
import AccessDeniedPage from '../pages/AccessDeniedPage';
import DashboardPage from '../pages/DashboardPage';
import SitesPage from '../pages/SitesPage';
import SiteReviewPage from '../pages/SiteReviewPage';
import PagesPage from '../pages/PagesPage';
import AuditPage from '../pages/AuditPage';
import AuditResultPage from '../pages/AuditResultPage';
import BlocksPage from '../pages/BlocksPage';
import BlockDetailsPage from '../pages/BlockDetailsPage';
import RuleSeverityPage from '../pages/RuleSeverityPage';
import WebhooksPage from '../pages/WebhooksPage';
import AiQueryPage from '../pages/AiQueryPage';
import AllowedUsersPage from '../pages/AllowedUsersPage';
import AdminPage from '../pages/AdminPage';
import { useApiAuth } from '../hooks/useApiAuth';
import { useUserRole } from '../hooks/useUserRole';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // TODO: re-enable auth after accessibility audit — remove the early return below
  // return <>{children}</>;

  // eslint-disable-next-line no-unreachable
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isApiAuthReady } = useApiAuth();
  const role = useUserRole(isApiAuthReady);

  if (role === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (role !== 'admin') {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="rule-severities" element={<RuleSeverityPage />} />
        <Route path="sites/:siteId/review" element={<SiteReviewPage />} />
        <Route path="sites/:siteId/pages" element={<PagesPage />} />
        <Route path="sites/:siteId/pages/:pageId/audit" element={<AuditPage />} />
        <Route path="audits/:auditId" element={<AuditResultPage />} />
        <Route path="blocks" element={<BlocksPage />} />
        <Route path="blocks/details" element={<BlockDetailsPage />} />
        <Route path="ai-query" element={<AiQueryPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/allowed-users"
          element={
            <AdminRoute>
              <AllowedUsersPage />
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  );
}
